import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTool, useCreateTool, useUpdateTool } from '../../hooks/useTools';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Button, Card, CardHeader, Input, Select, Textarea } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import type {
  ToolType,
  ToolStatus,
  McpTransport,
  ToolInputSchema,
  CreateToolInput,
  UpdateToolInput,
  PlatformTool,
  McpTool,
} from '../../types';

const TOOL_TYPE_OPTIONS = [
  { value: 'platform', label: 'Platform Tool' },
  { value: 'mcp', label: 'MCP Tool' },
];

const TRANSPORT_OPTIONS = [
  { value: 'sse', label: 'Server-Sent Events (SSE)' },
  { value: 'websocket', label: 'WebSocket' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' },
];

const DEFAULT_SCHEMA: ToolInputSchema = {
  type: 'object',
  properties: {},
  required: [],
};

export default function ToolForm() {
  const { toolId } = useParams<{ toolId: string }>();
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();

  const isEditing = !!toolId;

  const { data: existingTool, isLoading: toolLoading } = useTool(toolId);
  const createTool = useCreateTool();
  const updateTool = useUpdateTool();

  const [formData, setFormData] = useState({
    type: 'platform' as ToolType,
    name: '',
    description: '',
    inputSchema: JSON.stringify(DEFAULT_SCHEMA, null, 2),
    status: 'active' as ToolStatus,
    group: '',
    displayOrder: '',
    // Platform tool fields
    handlerId: '',
    handlerConfig: '',
    // MCP tool fields
    serverUrl: '',
    transport: 'sse' as McpTransport,
    remoteToolName: '',
    timeoutMs: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (existingTool && isEditing) {
      const isPlatform = existingTool.type === 'platform';
      const platformTool = existingTool as PlatformTool;
      const mcpTool = existingTool as McpTool;

      setFormData({
        type: existingTool.type,
        name: existingTool.name,
        description: existingTool.description,
        inputSchema: JSON.stringify(existingTool.inputSchema, null, 2),
        status: existingTool.status,
        group: existingTool.group || '',
        displayOrder: existingTool.displayOrder != null
          ? existingTool.displayOrder.toString()
          : '',
        handlerId: isPlatform ? platformTool.handlerId : '',
        handlerConfig: isPlatform && platformTool.handlerConfig
          ? JSON.stringify(platformTool.handlerConfig, null, 2)
          : '',
        serverUrl: !isPlatform ? mcpTool.serverUrl : '',
        transport: !isPlatform ? mcpTool.transport : 'sse',
        remoteToolName: !isPlatform ? mcpTool.remoteToolName : '',
        timeoutMs: !isPlatform && mcpTool.timeoutMs
          ? mcpTool.timeoutMs.toString()
          : '',
      });
    }
  }, [existingTool, isEditing]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    try {
      const schema = JSON.parse(formData.inputSchema);
      if (schema.type !== 'object') {
        newErrors.inputSchema = 'Schema root type must be "object"';
      }
    } catch {
      newErrors.inputSchema = 'Invalid JSON';
    }

    if (formData.type === 'platform') {
      if (!formData.handlerId.trim()) {
        newErrors.handlerId = 'Handler ID is required';
      }
      if (formData.handlerConfig) {
        try {
          JSON.parse(formData.handlerConfig);
        } catch {
          newErrors.handlerConfig = 'Invalid JSON';
        }
      }
    } else {
      if (!formData.serverUrl.trim()) {
        newErrors.serverUrl = 'Server URL is required';
      }
      if (!formData.remoteToolName.trim()) {
        newErrors.remoteToolName = 'Remote tool name is required';
      }
      if (formData.timeoutMs) {
        const timeout = parseInt(formData.timeoutMs);
        if (isNaN(timeout) || timeout < 1) {
          newErrors.timeoutMs = 'Timeout must be a positive number';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const inputSchema = JSON.parse(formData.inputSchema) as ToolInputSchema;

    try {
      if (isEditing && toolId) {
        const input: UpdateToolInput = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          inputSchema,
          status: formData.status,
          group: formData.group.trim() || undefined,
          displayOrder: formData.displayOrder
            ? parseInt(formData.displayOrder)
            : undefined,
        };

        if (existingTool?.type === 'platform') {
          input.handlerId = formData.handlerId.trim();
          input.handlerConfig = formData.handlerConfig
            ? JSON.parse(formData.handlerConfig)
            : undefined;
        } else {
          input.serverUrl = formData.serverUrl.trim();
          input.transport = formData.transport;
          input.remoteToolName = formData.remoteToolName.trim();
          input.timeoutMs = formData.timeoutMs
            ? parseInt(formData.timeoutMs)
            : undefined;
        }

        await updateTool.mutateAsync({ toolId, input });
        success('Tool updated successfully');
        navigate(routes.console.spaceTool(spaceId, toolId!));
      } else {
        let input: CreateToolInput;

        if (formData.type === 'platform') {
          input = {
            type: 'platform',
            name: formData.name.trim(),
            description: formData.description.trim(),
            inputSchema,
            handlerId: formData.handlerId.trim(),
            handlerConfig: formData.handlerConfig
              ? JSON.parse(formData.handlerConfig)
              : undefined,
            group: formData.group.trim() || undefined,
            displayOrder: formData.displayOrder
              ? parseInt(formData.displayOrder)
              : undefined,
          };
        } else {
          input = {
            type: 'mcp',
            name: formData.name.trim(),
            description: formData.description.trim(),
            inputSchema,
            serverUrl: formData.serverUrl.trim(),
            transport: formData.transport,
            remoteToolName: formData.remoteToolName.trim(),
            timeoutMs: formData.timeoutMs
              ? parseInt(formData.timeoutMs)
              : undefined,
            group: formData.group.trim() || undefined,
            displayOrder: formData.displayOrder
              ? parseInt(formData.displayOrder)
              : undefined,
          };
        }

        const newTool = await createTool.mutateAsync(input);
        success('Tool created successfully');
        navigate(routes.console.spaceTool(spaceId, newTool.toolId));
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save tool');
    }
  };

  if (isEditing && toolLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const isSubmitting = createTool.isPending || updateTool.isPending;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {isEditing ? 'Edit Tool' : 'Create Tool'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isEditing ? 'Update your tool configuration' : 'Create a new tool'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <Card>
          <CardHeader title="Basic Information" />
          <div className="space-y-4">
            {!isEditing && (
              <Select
                label="Tool Type"
                options={TOOL_TYPE_OPTIONS}
                value={formData.type}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({
                    ...prev,
                    type: e.target.value as ToolType,
                  }))
                }
              />
            )}
            <Input
              label="Name"
              required
              value={formData.name}
              onChange={(e: { target: { value: string } }) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              error={errors.name}
              placeholder="my_tool"
            />
            <Textarea
              label="Description"
              required
              value={formData.description}
              onChange={(e: { target: { value: string } }) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              error={errors.description}
              placeholder="What does this tool do?"
              rows={2}
            />
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Group"
                value={formData.group}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({ ...prev, group: e.target.value }))
                }
                placeholder="email"
                hint="e.g. email, kb, jira, calendar, utility"
              />
              <Input
                label="Display Order"
                type="number"
                value={formData.displayOrder}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({ ...prev, displayOrder: e.target.value }))
                }
                placeholder="10"
                hint="Lower = first"
              />
            </div>
            {isEditing && (
              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={formData.status}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: e.target.value as ToolStatus,
                  }))
                }
              />
            )}
          </div>
        </Card>

        {/* Type-specific configuration */}
        {formData.type === 'platform' ? (
          <Card>
            <CardHeader
              title="Platform Tool Configuration"
              description="Configure the handler for this platform tool"
            />
            <div className="space-y-4">
              <Input
                label="Handler ID"
                required
                value={formData.handlerId}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({ ...prev, handlerId: e.target.value }))
                }
                error={errors.handlerId}
                placeholder="resolve_datetime"
                hint="The ID of the registered platform tool handler"
              />
              <Textarea
                label="Handler Config (JSON, optional)"
                value={formData.handlerConfig}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({
                    ...prev,
                    handlerConfig: e.target.value,
                  }))
                }
                error={errors.handlerConfig}
                placeholder="{}"
                rows={4}
                className="font-mono text-sm"
              />
            </div>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title="MCP Tool Configuration"
              description="Configure the MCP server connection"
            />
            <div className="space-y-4">
              <Input
                label="Server URL"
                required
                value={formData.serverUrl}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({ ...prev, serverUrl: e.target.value }))
                }
                error={errors.serverUrl}
                placeholder="https://mcp.example.com"
              />
              <Select
                label="Transport"
                options={TRANSPORT_OPTIONS}
                value={formData.transport}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({
                    ...prev,
                    transport: e.target.value as McpTransport,
                  }))
                }
              />
              <Input
                label="Remote Tool Name"
                required
                value={formData.remoteToolName}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({
                    ...prev,
                    remoteToolName: e.target.value,
                  }))
                }
                error={errors.remoteToolName}
                placeholder="send_email"
                hint="The name of the tool on the MCP server"
              />
              <Input
                label="Timeout (ms)"
                type="number"
                value={formData.timeoutMs}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({ ...prev, timeoutMs: e.target.value }))
                }
                error={errors.timeoutMs}
                placeholder="30000"
              />
            </div>
          </Card>
        )}

        {/* Input schema */}
        <Card>
          <CardHeader
            title="Input Schema"
            description="JSON Schema defining the tool's input parameters"
          />
          <Textarea
            required
            value={formData.inputSchema}
            onChange={(e: { target: { value: string } }) =>
              setFormData((prev) => ({ ...prev, inputSchema: e.target.value }))
            }
            error={errors.inputSchema}
            rows={12}
            className="font-mono text-sm"
          />
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Link to={isEditing ? routes.console.spaceTool(spaceId, toolId!) : routes.console.spaceTools(spaceId)}>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
          <Button type="submit" isLoading={isSubmitting}>
            {isEditing ? 'Save Changes' : 'Create Tool'}
          </Button>
        </div>
      </form>
    </div>
  );
}
